/**
 * apps/web/src/pages/analyticsRanges.ts
 * -------------------------------------------------------
 * Shared helpers for the Analytics date range selector so the UI and
 * loader stay in sync on accepted values and defaults.
 */

import {
  addCalendarDays,
  formatCalendarDate,
  getCalendarDateInAppTimeZone,
} from '@tinypro-audit/shared/time';

/** Available date range buckets for Analytics. */
export const ANALYTICS_RANGE_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: 'Week-to-date', value: 'week-to-date' },
  { label: 'Last 7 days', value: 'last-7-days' },
] as const;

/** Union of the range values used by the selector + API query. */
export type AnalyticsRange = (typeof ANALYTICS_RANGE_OPTIONS)[number]['value'];

/** Default selection when none is provided. */
export const DEFAULT_ANALYTICS_RANGE: AnalyticsRange = 'week-to-date';

/** Normalize an arbitrary query param to a known range bucket. */
export function normalizeAnalyticsRange(raw: string | null): AnalyticsRange {
  return (ANALYTICS_RANGE_OPTIONS.find((opt) => opt.value === raw)?.value
    ?? DEFAULT_ANALYTICS_RANGE) as AnalyticsRange;
}

/** Human-readable date range string for the current week (Mon–today, Pacific Time). */
export function getWeekToDateRangeLabel(now: Date = new Date()): string {
  const end = getCalendarDateInAppTimeZone(now);
  const diffToMonday = (end.getUTCDay() + 6) % 7;
  const start = addCalendarDays(end, -diffToMonday);

  return `${formatCalendarDate(start)} – ${formatCalendarDate(end)}`;
}

/** Human-readable date range string for the trailing 7-day window ending today in Pacific Time. */
export function getLast7DaysRangeLabel(now: Date = new Date()): string {
  const end = getCalendarDateInAppTimeZone(now);
  const start = addCalendarDays(end, -6);

  return `${formatCalendarDate(start)} – ${formatCalendarDate(end)}`;
}
